import { useForm } from "@tellescope/forms";
import { messageToHtml, toTemplateContentFields, TemplateEditorValue } from "@tellescope/templates";
import {
  useCachedTags,
  useRolesQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUploadTellescopeFile,
} from "@tellescope/tanstack-query";
import { ListQueryQualifier, MessageTemplateType } from "@tellescope/types-models";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const TEMPLATE_TYPE_VALUES: MessageTemplateType[] = ["enduser", "Reply", "team"];
const asTemplateType = (value: string): MessageTemplateType | undefined =>
  TEMPLATE_TYPE_VALUES.find((known) => known === value);

const asQualifier = (value: string): ListQueryQualifier => (value === "All Of" ? "All Of" : "One Of");

/**
 * Whether the template's selected channels can deliver HTML. Only SMS is structurally plaintext-only;
 * every other channel (and an as-yet-unrestricted template with no channels selected) supports HTML.
 * Drives the editor's rich-vs-plaintext "Text" surface and the save-time content derivation.
 */
const channelsSupportHtml = (forChannels: string[]): boolean =>
  !(forChannels.length > 0 && forChannels.every((channel) => channel === "SMS"));

export interface UseTemplateFormDialogArgs {
  templateId?: string;
  mode?: "create" | "duplicate" | "edit";
  onSuccess?: () => void;
}

export const useTemplateFormDialog = ({ templateId, mode, onSuccess }: UseTemplateFormDialogArgs) => {
  const { t } = useTranslation("templates");
  const templateQuery = useTellescopeQuery({
    model: "templates",
    method: "getOne",
    request: templateId ?? "",
    enabled: !!templateId,
  });

  const [_newImageUrls, setImageUrls] = useState(templateQuery.data?.mmsAttachmentURLs);
  const imageUrls = _newImageUrls ?? templateQuery.data?.mmsAttachmentURLs;
  const { uploadFileAsync } = useUploadTellescopeFile({ returnPublicURL: true });

  const handleAddImage = useCallback(
    async (file: File) => {
      const { publicURL } = await uploadFileAsync(
        { name: file.name, size: file.size, type: file.type, publicRead: true },
        file
      );
      if (publicURL) setImageUrls((prev = imageUrls) => [...(prev ?? []), publicURL]);
    },
    [imageUrls, uploadFileAsync]
  );
  const handleRemoveImage = useCallback(
    async (url: string) => setImageUrls((prev = imageUrls) => prev?.filter((u) => u !== url) ?? []),
    [imageUrls]
  );

  // An edit updates in place; create and duplicate both persist a new template.
  const createMutation = useTellescopeMutation({
    model: "templates",
    method: "createOne",
    onSuccess,
    onError: () => toast.error(t("Failed to save template. Please try again.")),
  });
  const updateMutation = useTellescopeMutation({
    model: "templates",
    method: "updateOne",
    onSuccess,
    onError: () => toast.error(t("Failed to save template. Please try again.")),
  });

  // Memoized so `useForm` receives a stable `fields` identity across keystrokes; re-derived only
  // when the loaded template changes (which is what should re-seed untouched fields' initial values).
  const fields = useMemo(
    () =>
      ({
        title: {
          type: "text",
          initialValue: mode === "duplicate" ? `${templateQuery.data?.title} (copy)` : templateQuery.data?.title,
        },
        type: { type: "select", initialValue: templateQuery.data?.type || "enduser" },
        mode: { type: "select", initialValue: templateQuery.data?.mode ?? "richtext" },
        forChannels: { type: "multiselect", initialValue: templateQuery.data?.forChannels ?? [] },
        forRoles: { type: "multiselect", initialValue: templateQuery.data?.forRoles ?? [] },
        forEntityTypes: { type: "multiselect", initialValue: templateQuery.data?.forEntityTypes ?? [] },
        "forUserTags.values": { type: "multiselect", initialValue: templateQuery.data?.forUserTags?.values ?? [] },
        "forUserTags.qualifier": {
          type: "select",
          initialValue: templateQuery.data?.forUserTags?.qualifier ?? "One Of",
        },
        subject: { type: "text", initialValue: templateQuery.data?.subject },
        isMarketing: { type: "checkbox", initialValue: templateQuery.data?.isMarketing ?? false },
        hideFromCompose: { type: "checkbox", initialValue: templateQuery.data?.hideFromCompose ?? false },
        tags: { type: "multiselect", initialValue: templateQuery.data?.tags ?? [] },
        message: { type: "textarea", initialValue: templateQuery.data?.message, required: true },
        html: { type: "textarea", initialValue: templateQuery.data?.html, required: true },
        editorState: { type: "textarea", initialValue: templateQuery.data?.editorState },
        mmsAttachmentURLs: { type: "multiselect", initialValue: templateQuery.data?.mmsAttachmentURLs ?? [] },
      }) as const,
    [
      mode,
      templateQuery.data?.editorState,
      templateQuery.data?.forChannels,
      templateQuery.data?.forEntityTypes,
      templateQuery.data?.forRoles,
      templateQuery.data?.forUserTags?.qualifier,
      templateQuery.data?.forUserTags?.values,
      templateQuery.data?.hideFromCompose,
      templateQuery.data?.html,
      templateQuery.data?.isMarketing,
      templateQuery.data?.message,
      templateQuery.data?.mmsAttachmentURLs,
      templateQuery.data?.mode,
      templateQuery.data?.subject,
      templateQuery.data?.tags,
      templateQuery.data?.title,
      templateQuery.data?.type,
    ]
  );

  const form = useForm({
    fields,
    onSubmit: (values) => {
      const includeSMSFields = !values.forChannels.length || values.forChannels.includes("SMS");
      const includeEmailFields = !values.forChannels.length || values.forChannels.includes("Email");

      // When the selection can't deliver HTML (SMS-only), the basic text surface edited `message`
      // while `html` was intentionally left stale — so derive html from message on save. Otherwise
      // `message` was already derived live from `html`, so keep html as authored.
      const htmlSupported = channelsSupportHtml(values.forChannels);
      const html = htmlSupported ? values.html ?? "" : messageToHtml(values.message ?? "");

      // The mode↔field coupling (message derivation for html mode, always-stamped sentinel) is owned
      // by the canonical helper — never hand-rolled here.
      const content = toTemplateContentFields({
        message: values.message ?? "",
        html,
        editorState: values.editorState,
        mode: values.mode === "html" ? "html" : "richtext",
      });
      const payload = {
        title: values.title,
        type: asTemplateType(values.type),
        forChannels: values.forChannels,
        forRoles: values.forRoles,
        forEntityTypes: values.forEntityTypes,
        forUserTags: {
          values: values["forUserTags.values"],
          qualifier: asQualifier(values["forUserTags.qualifier"]),
        },
        subject: includeEmailFields ? values.subject : "",
        isMarketing: values.isMarketing,
        hideFromCompose: values.hideFromCompose,
        tags: values.tags,
        mmsAttachmentURLs: includeSMSFields ? values.mmsAttachmentURLs : [],
        ...content,
      };

      if (mode === "edit" && templateId)
        updateMutation.mutate({ id: templateId, ...payload, _options: { replaceObjectFields: true } });
      else createMutation.mutate(payload);
    },
    validators: {
      subject: (value, allValues) => {
        const supportsEmail = !allValues.forChannels.length || allValues.forChannels.includes("Email");
        if (supportsEmail && !value) {
          return t("Email subject is required");
        }
        return undefined;
      },
    },
  });

  const { resetForm, setFieldValue } = form;

  const handleReset = useCallback(() => {
    resetForm();
    setImageUrls(undefined);
  }, [resetForm]);

  const editorValue = useMemo<TemplateEditorValue>(
    () => ({
      message: form.values.message,
      html: form.values.html,
      editorState: form.values.editorState,
      mode: form.values.mode === "richtext" ? "richtext" : "html",
    }),
    [form.values.message, form.values.html, form.values.editorState, form.values.mode]
  );

  const onEditorChange = useCallback(
    (val: TemplateEditorValue) => {
      setFieldValue("message", val.message);
      setFieldValue("html", val.html);
      if (val.editorState) setFieldValue("editorState", val.editorState);
      setFieldValue("mode", val.mode);
    },
    [setFieldValue]
  );

  const richTextEnabled = useMemo(() => channelsSupportHtml(form.values.forChannels), [form.values.forChannels]);

  const { roles } = useRolesQuery();
  const entityTypeQuery = useTellescopeInfiniteQuery({
    model: "enduser_custom_types",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });
  const { data: fetchedUserTags } = useCachedTags({ model: "users" });
  const [createdUserTags, setCreatedUserTags] = useState<string[]>([]);
  const addUserTag = useCallback(
    (tag: string) => setCreatedUserTags((prev) => (prev.includes(tag) ? prev : [...prev, tag])),
    []
  );
  const userTags = useMemo(
    () => [...(fetchedUserTags ?? []), ...createdUserTags.filter((tag) => !fetchedUserTags?.includes(tag))],
    [fetchedUserTags, createdUserTags]
  );

  // Option lists and the tag creator are memoized so their identities stay stable across keystrokes;
  // that lets the (memoized) MultiSelects skip re-rendering when an unrelated field changes.
  const entityTypes = useMemo(() => entityTypeQuery.data ?? [], [entityTypeQuery.data]);
  const channelOptions = useMemo(
    () => [
      { value: "SMS", label: t("SMS") },
      { value: "Email", label: t("Email") },
      { value: "Chat", label: t("Chat") },
    ],
    [t]
  );
  const roleOptions = useMemo(() => roles.map((role) => ({ value: role.role, label: role.role })), [roles]);
  const entityTypeOptions = useMemo(
    () => [
      { value: "Default", label: t("Default") },
      ...entityTypes.map((entityType) => ({ value: entityType.id, label: entityType.title })),
    ],
    [t, entityTypes]
  );
  const userTagOptions = useMemo(() => userTags.map((tag) => ({ value: tag, label: tag })), [userTags]);
  const userTagCreator = useMemo(
    () => ({
      render: (inputValue: string) => t('Add "{{value}}"', { value: inputValue }),
      isValidNewOption: (inputValue: string, selectedValues: string[]) =>
        !!inputValue.trim() && !selectedValues.includes(inputValue.trim()),
      transform: (inputValue: string) => inputValue.trim(),
      onCreate: (tag: { value: string }) => addUserTag(tag.value),
    }),
    [t, addUserTag]
  );

  return {
    form,
    template: templateQuery.data,
    contentLoading: !!templateId && (form.values.message === undefined || form.values.html === undefined),
    richTextEnabled,
    editorValue,
    onEditorChange,
    imageUrls,
    handleAddImage,
    handleRemoveImage,
    handleReset,
    isSaving: createMutation.isPending || updateMutation.isPending,
    channelOptions,
    roleOptions,
    entityTypeOptions,
    userTagOptions,
    userTagCreator,
  };
};
